import React from 'react';
import { X } from 'lucide-react';
import useModalEscape from '../../hooks/useModalEscape';
import useModalTransition from '../../hooks/useModalTransition';
import './ScopeLimitationsModal.css';

const ScopeLimitationsModal = ({ isOpen, onClose }) => {
  const shouldRender = useModalTransition(isOpen);
  useModalEscape(onClose, isOpen);

  if (!shouldRender) return null;

  return (
    <div className={`privacy-modal-overlay modal-transition-${isOpen ? 'enter' : 'exit'}`} onClick={onClose}>
      <div className="privacy-modal-content settings-scope-modal" onClick={(event) => event.stopPropagation()}>
        <button className="privacy-modal-close" onClick={onClose} aria-label="Close Scope and Limitations">
          <X size={20} />
        </button>

        <div className="privacy-header">
          <h2 className="privacy-title" style={{ margin: 0 }}>Scope &amp; Limitations</h2>
          <p className="privacy-subtitle">Project coverage and defined boundaries</p>
        </div>

        <div className="privacy-body settings-scope-modal-body">
          <section className="settings-scope-section">
            <h3>Scope of the Project</h3>
            <p>This study focuses on the design, development, and technical validation of Avisa, an AI-integrated legal information system. It consists of a cross-platform mobile application for citizens and a web-based administrative control center for local officials, built on a Three-Tier Cloud-Native Architecture.</p>
            <p>Avisa uses Gemini 3.1 Flash Lite to transform unstructured legal documents into structured, bilingual English and Filipino data. The model was selected for its low-cost API rates, native multimodal capabilities, and fast output generation. The pilot covers national mother laws connected to local ordinances within one selected LGU and three pilot barangays for hyper-local ordinance mapping.</p>
            <p>The system includes a Context-Augmented Generation chat interface, role-based access control, real-time monitoring of national laws, and expert validation of AI-generated summaries. Barangay document representatives receive scoped view, edit, and upload privileges supported by audit logging, two-step validation, and appointment-based activation.</p>
            <p>The project also includes LGU onboarding through landing-page submissions and administrator-approved municipal account provisioning for multi-tenant scalability. Its credit-based model provides five free daily credits, reset at midnight PHT; text queries use one credit and image queries use three credits, with PayMongo and webhook-based balance updates supporting paid usage.</p>
          </section>

          <section className="settings-scope-section">
            <h3>Delimitations and Limitations</h3>
            <p>Manual data gathering and encoding are limited to one selected LGU, three selected pilot barangays within that municipality, and the mother laws connected to their local ordinances. The system supports future expansion through its multi-tenant architecture, but the following are outside this study:</p>
            <ul>
              <li>Manual gathering, scanning, and verification for non-pilot barangays or complete municipality-wide ordinance collections.</li>
              <li>Municipality-wide automated ordinance ingestion and verification.</li>
              <li>Official legal advice, legal filings, or replacement of formal legal processes and official acts of local legislatures.</li>
              <li>Offline AI operation, because Gemini API and Firebase Cloud services require internet connectivity.</li>
              <li>Public access to the administrative web application, which is restricted to authorized administrative users.</li>
            </ul>
            <p>Avisa is strictly an informational tool. AI-generated interpretations and plain-language summaries do not constitute legal advice. Legal experts will review outputs for accuracy, completeness, and safety, and their findings will guide further refinement before wider rollout.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ScopeLimitationsModal;
